/**
 * Abstract base class for streaming AI assistant panel components.
 *
 * Encapsulates all shared state management, streaming buffer logic,
 * and chat lifecycle handling. Subclasses only need to provide:
 * - Domain-specific service integration (performStreamingRequest)
 * - Domain-specific done/response handling (onStreamDone)
 * - Domain-specific message actions (getMessageActions)
 * - Configuration (hidden tool calls, tool call labels, etc.)
 */

import { EventEmitter } from '@angular/core';

import { AI_CONVERSATION_ROLES } from 'src/app/constants';

import { MessageService } from '../../../services/message.service';
import { BaseStreamCallbacks } from '../../../services/streaming-chat.service';

import { StreamingChatPanelComponent } from './streaming-chat-panel.component';
import type {
  StreamingChatMessage,
  StreamingChatMessageAction,
  SendMessagePayload,
  ContentPart,
  ToolCallInfo,
} from './streaming-chat-panel.types';

// ============================================================================
// Base Message Interface
// ============================================================================

/**
 * Base message interface that all domain-specific message types should extend.
 * Contains the common fields needed for streaming chat state management.
 */
export interface BaseAssistantMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: Date;
  isError?: boolean;
  thinkingContent?: string;
  thinkingDurationMs?: number;
  contentParts?: ContentPart[];
  isStreaming?: boolean;
  /** Base64 image strings attached to this message (user messages only) */
  images?: string[];
}

// ============================================================================
// Abstract Base Class
// ============================================================================

export abstract class StreamingChatPanelBase<TMessage extends BaseAssistantMessage> {
  // ============================================================================
  // Shared State
  // ============================================================================

  /** Conversation history */
  conversationHistory: TMessage[] = [];

  /** Currently streaming message */
  streamingMessage: StreamingChatMessage | null = null;

  /** Loading state */
  isLoading = false;

  /** Session ID */
  sessionId?: string;

  /** Queued message */
  queuedMessage?: string;

  /** Last user message (for retry) */
  protected lastUserMessage?: string;

  /** Abort controller for cancellation */
  protected abortController?: AbortController;

  /** Thinking start time for duration calculation */
  protected thinkingStartTime: number | null = null;

  /** Pending timeouts for cleanup */
  protected pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  /** Tool call counter for unique IDs */
  protected toolCallCounter = 0;

  /** Whether the last request was cancelled (orphaned user message) */
  protected wasCancelled = false;

  // ============================================================================
  // Streaming Batching (requestAnimationFrame)
  // ============================================================================

  /** Buffered text waiting to be flushed */
  protected textBuffer = '';

  /** Buffered thinking text waiting to be flushed */
  protected thinkingBuffer = '';

  /** Whether an RAF flush is scheduled */
  protected rafId: number | null = null;

  // ============================================================================
  // Abstract Members — Subclasses Must Implement
  // ============================================================================

  /** Reference to the chat panel component (for scroll-to-bottom). */
  protected abstract get chatPanelRef(): StreamingChatPanelComponent | undefined;

  /** Reference to the message service (for toast notifications). */
  protected abstract get messageService(): MessageService;

  /** Reference to the panelClose EventEmitter. */
  abstract get panelCloseEmitter(): EventEmitter<void>;

  /** Returns the set of tool names that should be hidden from the UI. */
  protected abstract getHiddenToolCalls(): Set<string>;

  /** Returns the display label for a tool call. */
  abstract getToolCallLabel(toolName: string, input?: unknown): string;

  /** Returns domain-specific action buttons for a message. */
  protected abstract getMessageActions(msg: TMessage, isLastMessage: boolean): StreamingChatMessageAction[];

  /**
   * Called when the stream completes successfully.
   * The base class has already finalized the streaming message and added it to history.
   * Use this to handle domain-specific response data (automation changes, code changes, etc.).
   */
  protected abstract onStreamDone(response: unknown): void;

  /**
   * Perform the actual streaming request using the domain-specific service.
   * The base class provides the standard callbacks; the subclass should spread them
   * and add any domain-specific callbacks (onAutomationChange, onCodeChange, etc.).
   */
  protected abstract performStreamingRequest(
    message: string,
    history: Array<{ role: 'user' | 'assistant'; content: string; timestamp?: Date }>,
    images: string[] | undefined,
    callbacks: BaseStreamCallbacks<unknown>,
    signal: AbortSignal,
  ): Promise<void>;

  // ============================================================================
  // Optional Hooks — Subclasses May Override
  // ============================================================================

  /**
   * Optional hook called during onToolCall, before generic processing.
   * Use for domain-specific side effects (e.g., showing diff from update_code input).
   */
  protected onToolCallHook(_toolName: string, _input: unknown): void {
    // No-op by default; override for domain-specific side effects
  }

  /**
   * Called during resetSession. Override to clear domain-specific state.
   */
  protected resetDomainState(): void {
    // Default no-op; subclasses override as needed
  }

  /**
   * Called at the start of sendRequest. Override to clear domain-specific per-request state.
   */
  protected resetPerRequestState(): void {
    // Default no-op; subclasses override as needed
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  /** Call from subclass ngOnDestroy */
  protected baseOnDestroy(): void {
    this.abortController?.abort();
    this.cancelStreamingFlush();
    this.pendingTimeouts.forEach((id) => clearTimeout(id));
    this.pendingTimeouts = [];
    this.resetSession();
  }

  // ============================================================================
  // Computed Properties
  // ============================================================================

  /** Get messages formatted for StreamingChatPanelComponent */
  get chatMessages(): StreamingChatMessage[] {
    const allMessages: Array<TMessage | StreamingChatMessage> = this.streamingMessage
      ? [...this.conversationHistory, this.streamingMessage]
      : [...this.conversationHistory];

    return allMessages.map((msg, index) => {
      const isLastMessage = index === allMessages.length - 1;
      const isStreamingMsg = !!(this.streamingMessage && isLastMessage && this.isLoading);

      // The streaming message stores data in metadata, while history messages
      // store it as top-level properties on TMessage. Read from the
      // correct location depending on which type we're dealing with.
      const thinkingContent = isStreamingMsg
        ? ((msg as StreamingChatMessage).metadata?.thinkingContent as string)
        : (msg as TMessage).thinkingContent;
      const thinkingDurationMs = isStreamingMsg
        ? ((msg as StreamingChatMessage).metadata?.thinkingDurationMs as number | undefined)
        : (msg as TMessage).thinkingDurationMs;
      const contentParts = isStreamingMsg
        ? ((msg as StreamingChatMessage).metadata?.contentParts as ContentPart[])
        : (msg as TMessage).contentParts;

      // Determine thinking state
      const isWaitingForContent = isStreamingMsg && !thinkingContent && !msg.content && !contentParts?.length;
      const hasActiveThinking = isStreamingMsg && this.thinkingStartTime !== null && !thinkingDurationMs;

      // Processing state (thinking done but no content yet)
      const isProcessing = isStreamingMsg && thinkingDurationMs !== undefined && !contentParts?.length && !msg.content;

      const chatMsg: StreamingChatMessage = {
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        images: (msg as TMessage).images,
        metadata: {
          isError: (msg as TMessage).isError,
          isStreaming: isStreamingMsg,
          thinkingContent,
          thinkingDurationMs,
          isThinkingActive: hasActiveThinking || isWaitingForContent,
          isProcessing,
          contentParts,
          messageIndex: index,
        },
        actions: this.getMessageActions(msg as TMessage, isLastMessage),
      };

      return chatMsg;
    });
  }

  // ============================================================================
  // Chat Panel Event Handlers
  // ============================================================================

  async onSendMessage(payload: SendMessagePayload): Promise<void> {
    if (!payload.text.trim() && !payload.images?.length) {
      return;
    }

    // If already loading, queue the message (text only)
    if (this.isLoading) {
      this.queuedMessage = payload.text.trim();

      return;
    }

    await this.sendRequest(payload.text.trim(), { images: payload.images });
  }

  onNewChat(): void {
    this.resetSession();
  }

  onCancelRequest(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
      this.isLoading = false;
      this.cancelStreamingFlush();
      this.streamingMessage = null;
      this.thinkingStartTime = null;
      this.textBuffer = '';
      this.thinkingBuffer = '';
      this.wasCancelled = true;

      // The aborted request's own cleanup starts what is queued (see `startNextTurn`).
      if (this.queuedMessage) {
        this.wasCancelled = false;
      }
    }
  }

  /**
   * A request has settled — finished, failed or aborted — and cleaned up:
   * start whatever is due next. By default that is the message the user
   * queued meanwhile; a panel that starts turns of its own decides their
   * order here.
   */
  protected startNextTurn(): void {
    if (this.queuedMessage) {
      const nextMessage = this.queuedMessage;
      this.queuedMessage = undefined;
      this.scheduleTimeout(() => this.sendRequest(nextMessage), 100);
    }
  }

  onClearQueuedMessage(): void {
    this.queuedMessage = undefined;
  }

  onRetryLastMessage(): void {
    if (this.lastUserMessage) {
      // Remove the error message from history
      const lastMessage = this.conversationHistory[this.conversationHistory.length - 1];

      if (lastMessage?.isError) {
        this.conversationHistory = this.conversationHistory.slice(0, -1);
      }

      this.sendRequest(this.lastUserMessage, { hidden: true });
    }
  }

  onResendLastMessage(): void {
    if (this.lastUserMessage) {
      this.wasCancelled = false;

      // The user message is already in history, just resend
      this.sendRequest(this.lastUserMessage, { hidden: true });
    }
  }

  onDeleteLastMessage(): void {
    this.wasCancelled = false;

    // Remove the last user message from history
    const lastMessage = this.conversationHistory[this.conversationHistory.length - 1];

    if (lastMessage?.role === 'user') {
      this.conversationHistory = this.conversationHistory.slice(0, -1);
    }
  }

  onPanelClose(): void {
    this.panelCloseEmitter.emit();
  }

  // ============================================================================
  // Core Request Logic
  // ============================================================================

  protected async sendRequest(message: string, options?: { hidden?: boolean; images?: string[] }): Promise<void> {
    this.isLoading = true;
    this.wasCancelled = false;
    this.resetPerRequestState();

    // Initialize streaming message
    this.streamingMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      metadata: {
        thinkingContent: '',
        contentParts: [],
        isStreaming: true,
      },
    };
    this.thinkingStartTime = null;
    this.toolCallCounter = 0;

    // Add user message unless hidden
    if (!options?.hidden) {
      this.lastUserMessage = message;
      this.conversationHistory = [
        ...this.conversationHistory,
        {
          role: 'user',
          content: message,
          timestamp: new Date(),
          images: options?.images,
        } as TMessage,
      ];
      this.chatPanelRef?.requestScrollToBottom(true);
    }

    // Accumulate all images from the conversation history + current message
    const allImages = [
      ...this.conversationHistory.flatMap((m) => m.images ?? []),
      ...(options?.hidden ? options?.images ?? [] : []),
    ];

    this.abortController = new AbortController();
    const hiddenToolCalls = this.getHiddenToolCalls();

    const baseCallbacks: BaseStreamCallbacks<unknown> = {
      onThinking: (content) => {
        if (!this.streamingMessage) {
          return;
        }

        if (this.thinkingStartTime === null) {
          this.thinkingStartTime = Date.now();
        }

        // Buffer thinking text and schedule a batched render
        this.thinkingBuffer += content;
        this.scheduleStreamingFlush();
      },

      onThinkingDone: () => {
        // Flush any buffered thinking content first
        this.flushStreamingBuffers();

        if (!this.streamingMessage || this.thinkingStartTime === null) {
          return;
        }

        const thinkingDuration = Date.now() - this.thinkingStartTime;
        this.streamingMessage = {
          ...this.streamingMessage,
          metadata: {
            ...this.streamingMessage.metadata,
            thinkingDurationMs: thinkingDuration,
          },
        };
        this.thinkingStartTime = null;
      },

      onText: (content) => {
        if (!this.streamingMessage) {
          return;
        }

        // Buffer text and schedule a batched render
        this.textBuffer += content;
        this.scheduleStreamingFlush();
      },

      onToolCall: (toolName, input) => {
        // Flush any buffered text before adding tool call
        this.flushStreamingBuffers();

        if (!this.streamingMessage) {
          return;
        }

        // Domain-specific hook (e.g., code-assistant shows diff from update_code input)
        this.onToolCallHook(toolName, input);

        // Skip hidden tool calls but add text separator
        if (hiddenToolCalls.has(toolName)) {
          const parts = [...((this.streamingMessage.metadata?.contentParts as ContentPart[]) || [])];
          const lastPart = parts[parts.length - 1];

          if (lastPart && lastPart.type === 'text' && lastPart.text.trim()) {
            parts.push({ type: 'text', text: '' });
            this.streamingMessage = {
              ...this.streamingMessage,
              metadata: {
                ...this.streamingMessage.metadata,
                contentParts: parts,
              },
            };
          }

          return;
        }

        const toolCall: ToolCallInfo = {
          id: `tool-${this.toolCallCounter++}`,
          toolName,
          input,
          status: 'pending',
        };

        const parts = [...((this.streamingMessage.metadata?.contentParts as ContentPart[]) || [])];
        parts.push({ type: 'tool-call', toolCall });

        this.streamingMessage = {
          ...this.streamingMessage,
          metadata: {
            ...this.streamingMessage.metadata,
            contentParts: parts,
          },
        };
      },

      onToolResult: (toolName) => {
        if (!this.streamingMessage || hiddenToolCalls.has(toolName)) {
          return;
        }

        let foundMatch = false;
        const updatedParts = ((this.streamingMessage.metadata?.contentParts as ContentPart[]) || []).map((part) => {
          if (
            part.type === 'tool-call' &&
            !foundMatch &&
            part.toolCall.toolName === toolName &&
            part.toolCall.status === 'pending'
          ) {
            foundMatch = true;

            return {
              type: 'tool-call' as const,
              toolCall: { ...part.toolCall, status: 'complete' as const },
            };
          }

          return part;
        });

        this.streamingMessage = {
          ...this.streamingMessage,
          metadata: {
            ...this.streamingMessage.metadata,
            contentParts: updatedParts,
          },
        };
      },

      onStepStart: () => {
        // Could track step boundaries if needed
      },

      onStepFinish: () => {
        // Flush any buffered text before step boundary
        this.flushStreamingBuffers();

        if (!this.streamingMessage) {
          return;
        }

        if (this.streamingMessage.content.trim()) {
          this.streamingMessage = {
            ...this.streamingMessage,
            content: this.streamingMessage.content + '\n\n',
          };
        }
      },

      onDone: (response: unknown) => {
        // Flush any remaining buffered text before finalizing
        this.flushStreamingBuffers();

        const responseObj = response as { sessionId?: string; message?: string };
        this.sessionId = responseObj.sessionId;

        if (this.streamingMessage) {
          const finalMessage: TMessage = {
            role: 'assistant',
            content: responseObj.message ?? '',
            timestamp: this.streamingMessage.timestamp,
            thinkingContent: this.streamingMessage.metadata?.thinkingContent as string,
            thinkingDurationMs:
              (this.streamingMessage.metadata?.thinkingDurationMs as number) ??
              (this.thinkingStartTime !== null ? Date.now() - this.thinkingStartTime : undefined),
            contentParts: this.streamingMessage.metadata?.contentParts as ContentPart[],
            isStreaming: false,
          } as TMessage;

          // Mark all pending tool calls as complete
          if (finalMessage.contentParts?.length) {
            finalMessage.contentParts = finalMessage.contentParts.map((part) => {
              if (part.type === 'tool-call' && part.toolCall.status === 'pending') {
                return {
                  type: 'tool-call' as const,
                  toolCall: { ...part.toolCall, status: 'complete' as const },
                };
              }

              return part;
            });
          }

          this.conversationHistory = [...this.conversationHistory, finalMessage];
        }

        this.streamingMessage = null;
        this.thinkingStartTime = null;

        this.onStreamDone(response);
      },

      onError: (errorMessage) => {
        this.cancelStreamingFlush();
        console.error('Streaming error:', errorMessage);

        this.conversationHistory = [
          ...this.conversationHistory,
          {
            role: AI_CONVERSATION_ROLES.ASSISTANT,
            content: 'Sorry, something went wrong while processing your request. Please try again.',
            timestamp: new Date(),
            isError: true,
          } as TMessage,
        ];

        this.streamingMessage = null;
        this.thinkingStartTime = null;
      },
    };

    try {
      const lastHistoryMessage = this.conversationHistory.at(-1);
      const historyMessages =
        lastHistoryMessage?.role === 'user' && lastHistoryMessage.content === message
          ? this.conversationHistory.slice(0, -1)
          : this.conversationHistory;
      const history = historyMessages.map((m) => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp,
      }));

      await this.performStreamingRequest(
        message,
        history,
        allImages.length > 0 ? allImages : undefined,
        baseCallbacks,
        this.abortController.signal,
      );
    } catch (error: unknown) {
      const err = error as Error;

      if (err?.name === 'AbortError') {
        this.streamingMessage = null;
        this.thinkingStartTime = null;

        return;
      }

      console.error('Streaming request failed:', error);

      let errorMsg = 'Sorry, something went wrong while processing your request.';

      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        errorMsg = 'Connection lost. Please check your internet connection and try again.';
      } else if (err?.message?.includes('network')) {
        errorMsg = 'Network error. Please check your connection and try again.';
      } else if (err?.message?.includes('timeout')) {
        errorMsg = 'Request timed out. Please try again.';
      }

      this.conversationHistory = [
        ...this.conversationHistory,
        {
          role: AI_CONVERSATION_ROLES.ASSISTANT,
          content: errorMsg,
          timestamp: new Date(),
          isError: true,
        } as TMessage,
      ];

      this.streamingMessage = null;
      this.thinkingStartTime = null;
    } finally {
      this.isLoading = false;
      this.abortController = undefined;
      this.startNextTurn();
    }
  }

  // ============================================================================
  // Session Management
  // ============================================================================

  protected resetSession(): void {
    this.cancelStreamingFlush();
    this.conversationHistory = [];
    this.sessionId = undefined;
    this.streamingMessage = null;
    this.thinkingStartTime = null;
    this.queuedMessage = undefined;
    this.lastUserMessage = undefined;
    this.wasCancelled = false;
    this.textBuffer = '';
    this.thinkingBuffer = '';
    this.resetDomainState();
  }

  // ============================================================================
  // Streaming Buffer Management
  // ============================================================================

  /**
   * Schedule a requestAnimationFrame to flush buffered text/thinking.
   * Multiple rapid callbacks (text-delta, thinking) accumulate in the buffer
   * and get applied in a single DOM update per frame, reducing change detection
   * overhead and making text appear to stream faster.
   */
  protected scheduleStreamingFlush(): void {
    if (this.rafId !== null) {
      return; // Already scheduled
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.flushStreamingBuffers();
    });
  }

  /** Flush any buffered text/thinking into the streaming message. */
  protected flushStreamingBuffers(): void {
    this.cancelStreamingFlush();

    if (!this.streamingMessage) {
      return;
    }

    const hasText = this.textBuffer.length > 0;
    const hasThinking = this.thinkingBuffer.length > 0;

    if (!hasText && !hasThinking) {
      return;
    }

    let updated = this.streamingMessage;

    // Apply buffered thinking content
    if (hasThinking) {
      const existingThinking = (updated.metadata?.thinkingContent as string) || '';
      updated = {
        ...updated,
        metadata: {
          ...updated.metadata,
          thinkingContent: existingThinking + this.thinkingBuffer,
        },
      };
      this.thinkingBuffer = '';
    }

    // Apply buffered text content
    if (hasText) {
      const newContent = updated.content + this.textBuffer;
      const parts = [...((updated.metadata?.contentParts as ContentPart[]) || [])];
      const lastPart = parts[parts.length - 1];

      if (lastPart && lastPart.type === 'text') {
        parts[parts.length - 1] = { type: 'text', text: lastPart.text + this.textBuffer };
      } else {
        parts.push({ type: 'text', text: this.textBuffer });
      }

      updated = {
        ...updated,
        content: newContent,
        metadata: {
          ...updated.metadata,
          contentParts: parts,
        },
      };
      this.textBuffer = '';
    }

    this.streamingMessage = updated;
    this.chatPanelRef?.requestScrollToBottom();
  }

  /** Cancel any pending RAF flush. */
  protected cancelStreamingFlush(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  protected sendHiddenFollowUp(message: string): void {
    this.scheduleTimeout(() => {
      this.sendRequest(message, { hidden: true });
    }, 100);
  }

  protected scheduleTimeout(fn: () => void, delay: number): void {
    const id = setTimeout(() => {
      this.pendingTimeouts = this.pendingTimeouts.filter((t) => t !== id);
      fn();
    }, delay);

    this.pendingTimeouts.push(id);
  }
}
